import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/system';
import { VALET, FONTS, TYPE } from '../../theme';
import { MOBILE } from './responsive';
import { CleaningCell, cleaningChipText, cleaningChipTone, nyTodayKey } from './cleaningDay';

const SectionTitle = styled(Typography)({
  ...TYPE.sectionTitle,
  marginBottom: '16px',
});

// On a phone the five-column table means dragging sideways to reach the one
// column you wanted. Each customer becomes a card instead: name and order count
// on the top line, then a phone number you can actually dial, then where they
// are and when they arrived.
const CustomerCard = styled(Box)({
  padding: '14px 14px',
  borderBottom: `1px solid ${VALET.border}`,
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  '&:last-of-type': { borderBottom: 'none' },
});

const CardTopLine = styled(Box)({
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: '10px',
});

const CardName = styled(Typography)({
  fontFamily: FONTS.sans,
  fontSize: '15px',
  fontWeight: 600,
  color: VALET.textPrimary,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const CardCount = styled(Typography)({
  fontFamily: FONTS.mono,
  fontSize: '13px',
  fontWeight: 600,
  color: VALET.textPrimary,
  flexShrink: 0,
});

// A tel: link, because the whole point of the console being on a phone is that
// the phone can then ring the customer.
const CardPhone = styled('a')({
  fontFamily: FONTS.mono,
  fontSize: '13px',
  color: VALET.green,
  textDecoration: 'none',
  width: 'fit-content',
});

const CardMeta = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  flexWrap: 'wrap',
  fontFamily: FONTS.mono,
  fontSize: '11.5px',
  color: VALET.textMuted,
});

const EmptyNote = styled(Typography)({
  fontFamily: FONTS.sans,
  fontSize: '13px',
  color: VALET.textMuted,
  textAlign: 'center',
  padding: '32px 16px',
});

const TableWrapper = styled(Box)({
  backgroundColor: VALET.surface,
  border: `1px solid ${VALET.border}`,
  borderRadius: '14px',
  overflow: 'hidden',
  boxShadow: VALET.shadow,
});

const StyledHeaderCell = styled(TableCell)({
  fontFamily: FONTS.mono,
  fontSize: '10px',
  color: VALET.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.16em',
  fontWeight: 500,
  padding: '14px 16px',
  '@media (max-width: 768px)': { padding: '10px 12px' },
  borderBottom: `1px solid ${VALET.border}`,
  backgroundColor: VALET.surfaceMuted,
});

const StyledCell = styled(TableCell)({
  padding: '14px 16px',
  '@media (max-width: 768px)': { padding: '10px 12px' },
  fontSize: '13px',
  fontFamily: FONTS.sans,
  color: VALET.textPrimary,
  borderBottom: `1px solid ${VALET.border}`,
});

// Same chip the valet roster filters with, so the two tabs read the same.
const FilterChip = styled(Box)(({ selected }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '3px 10px',
  borderRadius: '10px',
  fontSize: '11px',
  cursor: 'pointer',
  border: '0.5px solid',
  borderColor: selected ? '#1a1a1a' : '#e0ddd6',
  backgroundColor: selected ? '#1a1a1a' : '#ffffff',
  color: selected ? '#ffffff' : '#6b6b68',
  fontFamily: FONTS.mono,
  userSelect: 'none',
  whiteSpace: 'nowrap',
}));

const ChipCount = styled('span')(({ selected }) => ({
  fontSize: '10px',
  color: selected ? 'rgba(255,255,255,0.6)' : VALET.textDim,
}));

const FilterLabel = styled(Typography)({
  fontFamily: FONTS.mono,
  fontSize: '10px',
  textTransform: 'uppercase',
  letterSpacing: '0.14em',
  color: VALET.textMuted,
  marginRight: '2px',
});

const Note = styled(Typography)({
  fontFamily: FONTS.sans,
  fontSize: '12px',
  color: VALET.textMuted,
  marginTop: '-8px',
  marginBottom: '16px',
});

/* -------------------------------------------------------------------------- */
/* Platform                                                                    */
/* -------------------------------------------------------------------------- */

// Keys are what the API sends. That value is what the customer is on *now*,
// not only where they signed up: a Website account that has since registered a
// push token comes back as `ios`, because only the app registers one. See
// `services/signupPlatform.js` → displayPlatform. Order here is the order the
// filter chips appear in.
const PLATFORMS = {
  ios: { label: 'iPhone', bg: VALET.greenLight, fg: VALET.green },
  android: { label: 'Android', bg: VALET.infoBg, fg: VALET.info },
  web: { label: 'Website', bg: VALET.bronzeMuted, fg: VALET.bronze },
  business_web: { label: 'Front desk', bg: VALET.warningBg, fg: VALET.warning },
  unknown: { label: 'Unknown', bg: VALET.surfaceHover, fg: VALET.textMuted },
};
const PLATFORM_ORDER = ['ios', 'android', 'web', 'business_web', 'unknown'];

function platformKey(u) {
  const raw = (u.platform || u.signupPlatform || '').toLowerCase();
  return PLATFORMS[raw] ? raw : 'unknown';
}

// The backend only knows the platform for accounts made after it started
// recording one. Older accounts are worked out from push tokens and signup
// date, and are shown as an outline so a guess never looks like a fact.
function isInferred(u) {
  return u.platformSource === 'inferred';
}

const INFERRED_NOTE =
  'Worked out from this account’s history, not recorded at signup. ' +
  'Accounts created before August 2026 predate platform tracking.';

function PlatformTag({ user }) {
  const key = platformKey(user);
  const { label, bg, fg } = PLATFORMS[key];
  const inferred = isInferred(user);

  // A guess keeps the platform's colour so the column is still scannable, but
  // wears a dashed outline instead of a filled pill so it never reads as fact.
  const tag = (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        padding: '2px 9px',
        borderRadius: '10px',
        fontSize: '11px',
        fontFamily: FONTS.mono,
        letterSpacing: '0.02em',
        whiteSpace: 'nowrap',
        cursor: inferred ? 'help' : 'default',
        backgroundColor: inferred ? 'transparent' : bg,
        color: fg,
        opacity: inferred ? 0.75 : 1,
        border: inferred ? `0.5px dashed ${fg}` : '0.5px solid transparent',
      }}
    >
      {label}
    </Box>
  );

  if (!inferred) return tag;
  return (
    <Tooltip title={INFERRED_NOTE} placement="top" arrow>
      {tag}
    </Tooltip>
  );
}

/* -------------------------------------------------------------------------- */
/* Signup time                                                                 */
/* -------------------------------------------------------------------------- */

// `signedUpAt` is the timestamp inside the Mongo ObjectId, which every account
// has; `createdAt` only exists on accounts made after the field was added.
function signupDate(u) {
  const raw = u.signedUpAt || u.createdAt || u.signupDate;
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

function formatDay(d) {
  if (!d) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatClock(d) {
  if (!d) return '';
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

const RANGES = [
  { key: 'all', label: 'Any time' },
  { key: 'today', label: 'Today' },
  { key: '7', label: 'Last 7 days' },
  { key: '30', label: 'Last 30 days' },
  { key: '90', label: 'Last 90 days' },
];

// Cutoff a signup has to be at or after to count. Null means no cutoff.
// "Today" is local midnight, not 24 hours ago, so it matches what a day means
// to whoever is reading the screen.
function rangeCutoff(key) {
  if (key === 'all') return null;
  const now = new Date();
  if (key === 'today') {
    const midnight = new Date(now);
    midnight.setHours(0, 0, 0, 0);
    return midnight;
  }
  const days = Number(key);
  if (!days) return null;
  return new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
}

/* -------------------------------------------------------------------------- */

function extractUsers(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.users)) return data.users;
  if (Array.isArray(data.data)) return data.data;
  return [];
}

function matchesSearch(u, q) {
  if (!q) return true;
  const lower = q.toLowerCase();
  // 'tue' should find everyone swept on a Tuesday.
  if (u.cleaning && u.cleaning.shortLabel && u.cleaning.shortLabel.toLowerCase().includes(lower)) return true;
  return (
    (u.name || u.fullName || '').toLowerCase().includes(lower) ||
    (u.phone || u.phoneNumber || '').toLowerCase().includes(lower) ||
    (u.email || '').toLowerCase().includes(lower)
  );
}

// Whether the customer has told us their street-cleaning day. It is free and
// has nothing to do with a plan, so this is a different question from "are
// they a subscriber" and gets its own filter.
const CLEANING_FILTERS = [
  { key: 'all', label: 'Everyone', match: () => true },
  { key: 'set', label: 'Has a cleaning day', match: (u) => !!(u.cleaning && u.cleaning.hasSchedule) },
  { key: 'none', label: 'None set', match: (u) => !(u.cleaning && u.cleaning.hasSchedule) },
  {
    key: 'paused',
    label: 'Paused',
    match: (u) => !!(u.cleaning && u.cleaning.hasSchedule && !u.cleaning.active),
  },
];

export default function CustomersTab({ data }) {
  const [search, setSearch] = useState('');
  const [cleaningFilter, setCleaningFilter] = useState('all');
  const isMobile = useMediaQuery(MOBILE.replace('@media ', ''));
  const [platformFilter, setPlatformFilter] = useState('all');
  const [range, setRange] = useState('all');
  const [newestFirst, setNewestFirst] = useState(true);

  const users = useMemo(() => extractUsers(data), [data]);
  // New York's date, stamped by the server that books the moves. Older
  // payloads did not carry it, hence the fallback.
  const todayKey = (data && data.todayKey) || nyTodayKey();

  // Counts drive the chip labels, and only platforms that actually show up get
  // a chip — no dead "Android 0" sitting there until the day one exists.
  const platformCounts = useMemo(() => {
    const counts = {};
    users.forEach((u) => {
      const k = platformKey(u);
      counts[k] = (counts[k] || 0) + 1;
    });
    return counts;
  }, [users]);

  const platformChips = useMemo(
    () => PLATFORM_ORDER.filter((k) => platformCounts[k]),
    [platformCounts]
  );

  const inferredCount = useMemo(() => users.filter(isInferred).length, [users]);

  const cleaningCounts = useMemo(() => {
    const counts = {};
    CLEANING_FILTERS.forEach((f) => { counts[f.key] = users.filter(f.match).length; });
    return counts;
  }, [users]);

  const filtered = useMemo(() => {
    const cutoff = rangeCutoff(range);
    const rows = users.filter((u) => {
      if (platformFilter !== 'all' && platformKey(u) !== platformFilter) return false;
      const cleaningDef = CLEANING_FILTERS.find((f) => f.key === cleaningFilter);
      if (cleaningDef && !cleaningDef.match(u)) return false;
      if (cutoff) {
        const d = signupDate(u);
        if (!d || d < cutoff) return false;
      }
      return matchesSearch(u, search);
    });
    // The API already sorts newest first, but the sort has to be explicit for
    // the toggle to mean anything — and undated rows sink to the bottom either
    // way rather than jumping to the top of an oldest-first list.
    return rows.sort((a, b) => {
      const da = signupDate(a);
      const db = signupDate(b);
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return newestFirst ? db - da : da - db;
    });
  }, [users, platformFilter, cleaningFilter, range, search, newestFirst]);

  if (!data) return null;

  const isFiltered = filtered.length !== users.length;

  return (
    <Box>
      <SectionTitle>
        Customers ({filtered.length}{isFiltered ? ` of ${users.length}` : ''})
      </SectionTitle>

      {inferredCount > 0 && (
        <Note>
          A dashed platform is a best guess. Accounts made before the app started
          recording it are worked out from their history — {inferredCount} of{' '}
          {users.length} here. Every new signup from now on is recorded exactly.
        </Note>
      )}

      {/* Controls */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', mb: '16px' }}>
        <TextField
          size="small"
          placeholder="Search name, phone, or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            width: 360,
            maxWidth: '100%',
            '& .MuiOutlinedInput-root': {
              fontSize: '13px',
              borderRadius: '10px',
              fontFamily: FONTS.sans,
              backgroundColor: VALET.bg,
            },
          }}
        />

        {platformChips.length > 1 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <FilterLabel>Platform</FilterLabel>
            <FilterChip
              selected={platformFilter === 'all'}
              onClick={() => setPlatformFilter('all')}
            >
              All
              <ChipCount selected={platformFilter === 'all'}>{users.length}</ChipCount>
            </FilterChip>
            {platformChips.map((k) => (
              <FilterChip
                key={k}
                selected={platformFilter === k}
                onClick={() => setPlatformFilter(k)}
              >
                {PLATFORMS[k].label}
                <ChipCount selected={platformFilter === k}>{platformCounts[k]}</ChipCount>
              </FilterChip>
            ))}
          </Box>
        )}

        {cleaningCounts.set > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <FilterLabel>Cleaning day</FilterLabel>
            {CLEANING_FILTERS.map((f) => (
              <FilterChip
                key={f.key}
                selected={cleaningFilter === f.key}
                onClick={() => setCleaningFilter(f.key)}
              >
                {f.label}
                <ChipCount selected={cleaningFilter === f.key}>{cleaningCounts[f.key]}</ChipCount>
              </FilterChip>
            ))}
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          <FilterLabel>Signed up when</FilterLabel>
          {RANGES.map((r) => (
            <FilterChip key={r.key} selected={range === r.key} onClick={() => setRange(r.key)}>
              {r.label}
            </FilterChip>
          ))}
          <Box sx={{ width: '10px' }} />
          <FilterChip selected={false} onClick={() => setNewestFirst((v) => !v)}>
            {newestFirst ? 'Newest first' : 'Oldest first'}
          </FilterChip>
        </Box>
      </Box>

      <TableWrapper>
        {isMobile ? (
          <Box>
            {filtered.map((u, idx) => {
              const d = signupDate(u);
              const phone = u.phone || u.phoneNumber || '';
              const contact = phone || u.email || '';
              return (
                <CustomerCard key={u._id || u.id || idx}>
                  <CardTopLine>
                    <CardName>{u.name || u.fullName || '—'}</CardName>
                    <CardCount>
                      {u.totalRequests ?? u.requestCount ?? '—'}
                      <Box component="span" sx={{ color: VALET.textMuted, fontWeight: 400 }}>
                        {' '}
                        {(u.totalRequests ?? u.requestCount) === 1 ? 'order' : 'orders'}
                      </Box>
                    </CardCount>
                  </CardTopLine>

                  {contact ? (
                    phone ? (
                      <CardPhone href={`tel:${phone.replace(/[^\d+]/g, '')}`}>{phone}</CardPhone>
                    ) : (
                      <CardMeta>{contact}</CardMeta>
                    )
                  ) : null}

                  <CardMeta>
                    <PlatformTag user={u} />
                    <span>{formatDay(d)}</span>
                    {d && <span style={{ color: VALET.textDim }}>{formatClock(d)}</span>}
                  </CardMeta>

                  {cleaningChipText(u.cleaning, todayKey) && (
                    <CardMeta sx={{ color: cleaningChipTone(u.cleaning, todayKey) }}>
                      {cleaningChipText(u.cleaning, todayKey)}
                    </CardMeta>
                  )}
                </CustomerCard>
              );
            })}
            {filtered.length === 0 && (
              <EmptyNote>
                {users.length === 0 ? 'No customers found.' : 'Nothing matches those filters.'}
              </EmptyNote>
            )}
          </Box>
        ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 940 }}>
            <TableHead>
              <TableRow>
                <StyledHeaderCell>Name</StyledHeaderCell>
                <StyledHeaderCell>Contact</StyledHeaderCell>
                <StyledHeaderCell>Platform</StyledHeaderCell>
                <StyledHeaderCell>Cleaning day</StyledHeaderCell>
                <StyledHeaderCell>Signed up</StyledHeaderCell>
                <StyledHeaderCell align="right">Total requests</StyledHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((u, idx) => {
                const d = signupDate(u);
                return (
                  <TableRow key={u._id || u.id || idx} sx={{
                    '&:hover td': { backgroundColor: VALET.surfaceHover },
                    '&:last-child td': { borderBottom: 'none' },
                  }}>
                    <StyledCell sx={{ fontWeight: 600 }}>{u.name || u.fullName || '—'}</StyledCell>
                    <StyledCell sx={{ color: VALET.textSecondary }}>{u.phone || u.phoneNumber || u.email || '—'}</StyledCell>
                    <StyledCell>
                      <PlatformTag user={u} />
                    </StyledCell>
                    <StyledCell>
                      <CleaningCell cleaning={u.cleaning} todayKey={todayKey} />
                    </StyledCell>
                    <StyledCell sx={{ fontFamily: FONTS.mono, fontSize: '12px', color: VALET.textMuted, whiteSpace: 'nowrap' }}>
                      <Box>{formatDay(d)}</Box>
                      {d && (
                        <Box sx={{ fontSize: '11px', color: VALET.textDim, mt: '2px' }}>
                          {formatClock(d)}
                        </Box>
                      )}
                    </StyledCell>
                    <StyledCell align="right" sx={{ fontFamily: FONTS.mono, fontSize: '13px', fontWeight: 600 }}>
                      {u.totalRequests ?? u.requestCount ?? '—'}
                    </StyledCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && (
                <TableRow>
                  <StyledCell colSpan={6} sx={{ textAlign: 'center', color: VALET.textMuted, padding: '32px 16px' }}>
                    {users.length === 0 ? 'No customers found.' : 'Nothing matches those filters.'}
                  </StyledCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
        )}
      </TableWrapper>
    </Box>
  );
}
